import { useState, useEffect, useCallback, useMemo } from "react";
import { Typography, Box, Container } from "@mui/material";
import { GenerateInsightsFloatingButtons } from "../GenerateInsightData/FloatingButtons";
import { GeneralNewsTable } from "./News/General/GeneralNews";
import { CategorySpecificNewsTable } from "./News/CategorySpecific/CategoryNews";
import { FinancialReports } from "./FinancialReports/FinancialReports";
import { CostDriversSection } from "./CostDrivers/CostDrivers";
import { SectionNavBar } from "../../Navigation/SectionBar";
import { TaxonomyPath } from "../../ReusableComponents/TaxonomyPath";
import { FullWidthSection } from "../../ReusableComponents/FullWidthSection";
import { generateInsightSections } from "../../../utils/navigationPath";
import { generateMarketInsights } from "../../../utils/GenerateInsightFormHelpers";
import { deleteUserHistory, generatePowerPoint } from "../../../lib/api_routes";
import { SPACING } from "../../../utils/constants/designConstants";
import { SuppliersSection } from "./Suppliers/SuppliersSection";
import { SpendSection } from "./Spend/SpendSection";
import "./GenerateInsightsData.css";
import { MarketOverviewSection } from "./Market Overview/MarketOverviewSection";
import theme from "../../../theme";
import { useAccountInfo } from "../../../hooks/useAccountInfo";
import { formatAxiosError } from "../../../utils/error_message";
import { PFFReusableComponent } from "./Market Overview/PortersFiveForces/PFFReusableComponent";
import { SpendCostDriversSection } from "./Spend/Cost Drivers/SpendCostDriversSection";

export function GenerateInsightsData({
  costDrivers,
  newsAffectingPlans,
  sizeOfMarket,
  keyChallengesAndTrends,
  porterFiveForces,
  regulations,
  accreditations,
  generalNews,
  categorySpecificNews,
  suppliersToShow,
  existingSuppliers,
  selectedL3,
  financialReports,
  formValues,
  taxonomyPath,
  history,
  setHistory,
  trackAction,
  resetSession,
  snapshotSession,
  restoreSession,
  setCurrentEntryId,
  suppliers,
  isFormOpen,
  isGenerating,
  setIsFormOpen,
  isLoaded,
  setIsLoaded,
  setTaxonomyPath,
  setFormValues,
  setCostDrivers,
  setNewsAffectingPlans,
  setSizeOfMarket,
  setKeyChallengesAndTrends,
  setPorterFiveForces,
  setGeneralNews,
  setCategorySpecificNews,
  setFinancialReports,
  setSuppliers,
  setAccreditations,
  setRegulations,
  setSuppliersToShow,
  setExistingSuppliers,
  setGlobalSuppliers,
  setIsLoading,
  setLoadedCount,
  setTotalToLoad,
  setIsGenerating,
  handleSectionClick,
  spendOverview,
  spendBySupplier,
  spendByBusinessUnit,
  spendByDepartment,
  spendL3Insights,
  spendProcurementLevers,
  spendCostDrivers,
  spendInflationOverview,
  spendCommodityInflation,
  setSpendOverview,
  setSpendByBusinessUnit,
  setSpendByDepartment,
  setSpendBySupplier,
  setSpendL3Insights,
  setSpendProcurementLevers,
  setSpendCostDrivers,
  setSpendInflationOverview,
  setSpendCommodityInflation,
}) {
  const [historyId, setHistoryId] = useState(null);

  const { accountInfo } = useAccountInfo();

  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState(null);

  //-------------------------------------- history ----------------------------------------

  // Handler to open form - memoize to prevent unnecessary re-renders
  const openForm = useCallback(() => {
    setIsFormOpen(true);
    setIsLoaded(false);
    resetSession();
    if (typeof setTaxonomyPath === "function") {
      setTaxonomyPath("");
    }
  }, [setIsFormOpen, setIsLoaded, setTaxonomyPath, resetSession]);

  const formatSuppliersData = useCallback((data) => {
    if (!data) return [];
    return data.map((row) => ({
      "Supplier Name": row["Supplier"] || "",
      "National Status": row["National_Status"] || "",
      "Service Type": row["Service_Type"] || "",
      Website: row["Website"] || "",
      Accreditations: row["Accreditations"] || "",
      Turnover: row["Turnover"] || "",
      Description: row["Description"] || "",
    }));
  }, []);

  const formatExistingSuppliersData = useCallback((data) => {
    if (!data) return [];
    return data.map((row) => ({
      "Supplier Name": row["Normalised_Supplier_Names"] || "",
      Description: row.Description || "",
      "Category L1": row.L1 || "",
      "Category L2": row.L2 || "",
      "Category L3": row.L3 || "",
      "Category L4": row.L4 || "",
    }));
  }, []);

  // Function to rerun the insights generation from user history
  const rerunFromHistory = useCallback(
    async (entry) => {
      // Wait for accountInfo to be available
      if (!accountInfo?.accountId) return;

      let savedSessionId = null;

      const rerunFormValues = {
        category: entry.L1,
        L2: entry.L2,
        L3: entry.L3,
        L4: entry.L4,
        sector: entry.sector,
        country: entry.country,
        approximatespend: 0,
        context: entry.context || "",
      };

      try {
        setIsGenerating(true);
        setHistoryId(entry.id);
        setIsLoaded(false);

        // USER ACTIVITY TRACKING - Append "Rerun" to the existing session
        await trackAction(
          "Rerun From User History",
          "SUCCESS",
          null,
          rerunFormValues,
        );

        // Preserve the current session id for rerun history, so errors can be appended to the same session
        savedSessionId = snapshotSession();
        resetSession();

        setFormValues(rerunFormValues);

        // Options object for generateMarketInsights based on the parent setters
        const generateArgs = {
          values: { ...entry, category: entry.L1 },
          accountInfo,
          isValid: () => true,
          historyId: entry.id,
          setTaxonomyPath,
          setIsLoading,
          setIsLoaded,
          setLoadedCount,
          closeForm: () => {},
          setTotalToLoad,
          incrementLoadedCount: () => setLoadedCount((prev) => prev + 1),
          setIsGenerating,
          setHistory,
          setCurrentEntryId,
          setCostDrivers,
          setNewsAffectingPlans,
          setSizeOfMarket,
          setKeyChallengesAndTrends,
          setPorterFiveForces,
          setGeneralNews,
          setCategorySpecificNews,
          setFinancialReports,
          setSuppliers,
          setAccreditations,
          setRegulations,
          setSuppliersToShow,
          setExistingSuppliers,
          setGlobalSuppliers,
          formatSuppliersData,
          formatExistingSuppliersData,
          trackAction,
          resetSession,
          snapshotSession,
          restoreSession,
          onComplete: () => {
            setIsLoading(false);
            setIsLoaded(true);
            setIsGenerating(false);
          },
          setSpendOverview,
          setSpendBySupplier,
          setSpendByBusinessUnit,
          setSpendByDepartment,
          setSpendL3Insights,
          setSpendProcurementLevers,
          setSpendCostDrivers,
          setSpendInflationOverview,
          setSpendCommodityInflation,
        };

        const results = await generateMarketInsights(generateArgs);

        if (!results)
          throw new Error("generateMarketInsight returned undefined");

        const {
          costDrivers,
          newsData,
          sizeData,
          keyChallengesAndTrendsData,
          porterData,
          existingSuppliersData,
          generalNews,
          categorySpecificNews,
          financialReports,
          suppliersData,
          accreditationsData,
          regulationsData,
          suppliersToShowData,
          spendOverviewData,
          spendBySupplierData,
        } = results;

        setCostDrivers(costDrivers);
        setNewsAffectingPlans(newsData);
        setSizeOfMarket(sizeData);
        setKeyChallengesAndTrends(keyChallengesAndTrendsData);
        setPorterFiveForces(porterData);
        setGeneralNews(generalNews);
        setCategorySpecificNews(categorySpecificNews);
        setFinancialReports(financialReports);
        setSuppliers(suppliersData);
        setAccreditations(accreditationsData);
        setRegulations(regulationsData);
        setSuppliersToShow(formatSuppliersData(suppliersToShowData));
        setExistingSuppliers(
          formatExistingSuppliersData(existingSuppliersData),
        );
        setSpendOverview(spendOverviewData?.summary ?? null);
        setSpendBySupplier(spendBySupplierData?.data ?? null);
      } catch (error) {
        console.error("Error rerunning from history: ", error);

        // USER ACTIVITY TRACKING - Track failure
        // Restore the saved session so appendActivity works for rerun history
        restoreSession(savedSessionId);

        await trackAction(
          "Rerun From User History",
          "FAILED",
          error.message,
          rerunFormValues,
        );
        resetSession();
      } finally {
        setIsGenerating(false);
      }
    },
    [
      accountInfo,
      trackAction,
      resetSession,
      snapshotSession,
      restoreSession,
      setFormValues,
      setTaxonomyPath,
      setIsLoading,
      setIsLoaded,
      setLoadedCount,
      setTotalToLoad,
      setIsGenerating,
      setHistory,
      setCostDrivers,
      setNewsAffectingPlans,
      setSizeOfMarket,
      setKeyChallengesAndTrends,
      setPorterFiveForces,
      setGeneralNews,
      setCategorySpecificNews,
      setFinancialReports,
      setSuppliers,
      setAccreditations,
      setRegulations,
      setSuppliersToShow,
      setExistingSuppliers,
      setGlobalSuppliers,
      formatSuppliersData,
      formatExistingSuppliersData,
    ],
  );

  // Function to handle deleting an history item
  const handleDeleteHistoryItem = useCallback(
    async (itemId) => {
      try {
        if (itemId === historyId) {
          setHistoryId(null);
        }
        await deleteUserHistory(itemId);

        // USER ACTIVITY TRACKING - append delete history action
        await trackAction("Delete User History", "SUCCESS", null, formValues);

        // If successful, update the state
        setHistory((prevHistory) =>
          prevHistory.filter((item) => item.id !== itemId),
        );
      } catch (error) {
        const { fullError } = await formatAxiosError(error);
        console.error("Error deleting history item: ", error);
        await trackAction(
          "Delete User History",
          "FAILED",
          fullError,
          formValues,
        ).catch(console.error);
      }
    },
    [historyId, trackAction, formValues, setHistory],
  );

  //-------------- PPT export handlers------------
  const pptData = useMemo(
    () => ({
      NewsAffectingCategoryPlans: {
        mainContent: newsAffectingPlans?.mainText || newsAffectingPlans || "",
        furtherReading: Array.isArray(newsAffectingPlans?.furtherReading)
          ? newsAffectingPlans.furtherReading.join("\n")
          : "",
      },
      SizeOfMarket: {
        sections: sizeOfMarket?.sections || [],
        furtherReading: sizeOfMarket?.furtherReading || [],
      },
      keyChallengesAndTrends: {
        sections: keyChallengesAndTrends?.sections || [],
        furtherReading: keyChallengesAndTrends?.furtherReading || [],
      },
      PorterFiveForces: {
        sections: porterFiveForces?.analysis?.sections || [],
        furtherReading: porterFiveForces?.analysis?.furtherReading || [],
      },
      Regulations: regulations || [],
      Accreditations: accreditations || [],
      CostDrivers: costDrivers || [],
      Country: formValues.country,
      Sector: formValues.sector,
      Category: formValues.category,
      L2: formValues.L2,
      L3: formValues?.L3 || "",
      L4: formValues?.L4 || "",
    }),
    [
      newsAffectingPlans,
      sizeOfMarket,
      keyChallengesAndTrends,
      porterFiveForces,
      regulations,
      accreditations,
      costDrivers,
      formValues,
    ],
  );

  const handleGeneratePPTSuccess = useCallback(async (response) => {
    // Check if response.data exists and is a Blob

    if (!response?.data) {
      const err = new Error("No data received from server");
      err.details = response;
      throw err;
    }
    const blob =
      response.data instanceof Blob ? response.data : new Blob([response.data]);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "GeneratedPresentation.pptx");
    document.body.appendChild(link);
    link.click();
    // Clean up
    window.URL.revokeObjectURL(url);
    link.remove();

    // USER ACTIVITY TRACKING - success
    await trackAction("Export PowerPoint", "SUCCESS", null, formValues);

    setExportSuccess(true);
    setExportError(null);
  }, []);

  const handleGeneratePPTError = useCallback(
    async (error) => {
      const { fullError, serverMessage } = await formatAxiosError(error);

      const userMessage =
        serverMessage ||
        error.message ||
        "Failed to download PowerPoint. Please try again later.";

      await trackAction("Export PowerPoint", "FAILED", fullError, formValues);

      setExportError(userMessage);
      setExportSuccess(false);
      return userMessage;
    },
    [trackAction, formValues],
  );

  const handleExport = useCallback(async () => {
    const hasData =
      pptData.NewsAffectingCategoryPlans ||
      pptData.SizeOfMarket ||
      pptData.keyChallengesAndTrends ||
      pptData.PorterFiveForces ||
      pptData.CostDrivers?.length > 0;

    if (!hasData) {
      setExportError("No data available to export.");
      return;
    }

    setExporting(true);
    setExportError(null);
    setExportSuccess(false);
    generatePowerPoint(pptData)
      .then(handleGeneratePPTSuccess)
      .catch(handleGeneratePPTError)
      .finally(() => setExporting(false));
  }, [
    pptData,
    trackAction,
    resetSession,
    formValues,
    handleGeneratePPTSuccess,
    handleGeneratePPTError,
  ]);

  const marketOverviewSectionProps = {
    taxonomyPath,
    newsAffectingPlans,
    sizeOfMarket,
    keyChallengesAndTrends,
    porterFiveForces,
    regulations,
    accreditations,
  };

  //-------------- Section Nav Bar handle Spend section ------------
  const visibleSections = generateInsightSections.filter(
    (section) => section.id !== "spend" || accountInfo?.showSpend,
  );

  return (
    <>
      <SectionNavBar
        sections={visibleSections}
        onSectionClick={handleSectionClick}
        scrollThreshold={200}
      />
      {isLoaded && (
        <GenerateInsightsFloatingButtons
          isFormOpen={isFormOpen}
          exporting={exporting}
          exportSuccess={exportSuccess}
          exportError={exportError}
          openForm={openForm}
          setExportError={setExportError}
          setExportSuccess={setExportSuccess}
          onExport={handleExport}
          history={history}
          showHistoryPanelFloatingButton={
            isLoaded && history.length > 0 && !exporting
          }
          rerunFromHistory={rerunFromHistory}
          onDeleteHistoryItem={handleDeleteHistoryItem}
          currentlyGenerating={isGenerating}
          currentHistoryId={historyId}
        />
      )}
      <Container
        maxWidth={false}
        sx={{
          boxShadow: "none",
          m: 0,
          p: 0,
        }}
      >
        {/* Market Overview Section */}
        <MarketOverviewSection
          {...marketOverviewSectionProps}
          showPorterFiveForces={!accountInfo?.showSpend}
        />

        {/* Porter Five Forces */}
        {accountInfo?.showSpend === true && (
          <FullWidthSection
            backgroundColor="primary.main"
            borderRadiusBottomRight={theme.borderRadius.full}
          >
            <PFFReusableComponent porterFiveForces={porterFiveForces} />
          </FullWidthSection>
        )}

        {/* Spend Section - needs to be filtered by account*/}
        {accountInfo?.showSpend === true && (
          <FullWidthSection
            sx={{
              backgroundImage: `
                    url('/assets/spendTop.png'),
                    url('/assets/spendBottom.png')
                    `,
              backgroundPosition: `top center, bottom center`,
              backgroundSize: `cover, cover`,
            }}
          >
            <SpendSection
              taxonomyPath={taxonomyPath}
              spendOverview={spendOverview}
              supplierRows={spendBySupplier}
              businessUnitRows={spendByBusinessUnit}
              departmentRows={spendByDepartment}
              insights={spendL3Insights}
              procurementLevers={spendProcurementLevers}
            />
          </FullWidthSection>
        )}

        {/* Suppliers Section */}
        <SuppliersSection
          taxonomyPath={taxonomyPath}
          suppliersToShow={suppliersToShow}
          existingSuppliers={existingSuppliers}
        />

        {/* Cost Drivers Section */}
        <FullWidthSection
          sx={{
            backgroundImage: `
            url('/assets/costDriversTop.png'),
            url('/assets/costDriversBottom.png')
            `,
            backgroundPosition: `top center, bottom center`,
            backgroundSize: `cover, cover`,
          }}
        >
          <Box
            id="cost-drivers"
            component="section"
            aria-label="cost drivers"
            sx={{ display: "flex", flexDirection: "column" }}
            className="generate-insights-content-inside-box"
          >
            <Box sx={{ mb: SPACING.HEADING_BOTTOM, mt: SPACING.HEADING_TOP }}>
              <Typography variant="h1" component="h1">
                Cost Drivers
              </Typography>
              {/* Show taxonomy path */}
              <TaxonomyPath taxonomyPath={taxonomyPath} />
            </Box>
            {accountInfo?.showSpend === true ? (
              <SpendCostDriversSection
                costDrivers={spendCostDrivers}
                inflationOverview={spendInflationOverview}
                commodityInflation={spendCommodityInflation}
              />
            ) : (
              <CostDriversSection
                costDrivers={costDrivers}
                selectedL3={selectedL3}
              />
            )}
          </Box>
        </FullWidthSection>

        {/* News Section*/}
        <div>
          <Box
            id="news"
            component="section"
            aria-label="news"
            sx={{ display: "flex", flexDirection: "column" }}
            className="generate-insights-content-inside-box"
          >
            <Box sx={{ mb: SPACING.HEADING_BOTTOM, mt: SPACING.HEADING_TOP }}>
              <Typography variant="h1" component="h1">
                News
              </Typography>
              {/* Show taxonomy path */}
              <TaxonomyPath taxonomyPath={taxonomyPath} />
            </Box>
            <Box
              sx={{
                mb: SPACING.HEADING_BOTTOM,
                display: "flex",
                flexDirection: "column",
                maxWidth: "fit-content",
              }}
            >
              {/* Category Specific News */}
              <Box sx={{ mb: SPACING.SUBHEADING_BOTTOM }}>
                <CategorySpecificNewsTable
                  categorySpecificNews={categorySpecificNews}
                />
              </Box>
              {/* General News */}
              <Box>
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{ mb: SPACING.SUBHEADING_BOTTOM, color: "primary.brandSecondary" }}
                >
                  General News
                </Typography>
                {accountInfo && (
                  <GeneralNewsTable
                    generalNews={generalNews}
                    formValues={formValues}
                    accountInfo={accountInfo}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </div>

        {/* Financial Reporting Section */}
        <FullWidthSection
          backgroundColor="primary.main"
          borderRadiusTopRight={theme.borderRadius.full}
        >
          <Box
            id="financial-reporting"
            component="section"
            aria-label="financial reporting"
            sx={{ display: "flex", flexDirection: "column", color: "primary.white" }}
            className="generate-insights-content-inside-box"
          >
            <Box
              sx={{
                mb: SPACING.HEADING_BOTTOM,
                mt: SPACING.HEADING_TOP,
                display: "flex",
                flexDirection: "column",
                maxWidth: "fit-content",
              }}
            >
              <Typography variant="h1" component="h1">
                Financial Reporting
              </Typography>
            </Box>
            <FinancialReports financialReports={financialReports} />
          </Box>
        </FullWidthSection>
      </Container>
    </>
  );
}
